/**
 * Performance Forecasting Utilities
 * Contains algorithms for predicting future performance based on recent trends
 */

import { 
  predictNextOpponent, 
  calculateDayOfWeekFactors
} from './performanceForecastingHelpers';

/**
 * Calculate rolling averages for a player's recent games
 * @param {Array} games - Array of game objects in chronological order
 * @param {number} window - Number of games to include in the rolling window (default: 5)
 * @returns {Array} Array of rolling averages for each stat
 */
export const calculateRollingAverages = (games, window = 5) => {
  if (!games || games.length === 0) {
    return [];
  }

  // Sort games by date (ascending)
  const sortedGames = [...games].sort((a, b) => new Date(a.date) - new Date(b.date));
  
  // Initialize result array
  const rollingAverages = [];
  
  // Calculate rolling averages for each position in the array
  for (let i = 0; i < sortedGames.length; i++) {
    // Get the window of games for this calculation
    const startIdx = Math.max(0, i - window + 1);
    const windowGames = sortedGames.slice(startIdx, i + 1);
    
    // Skip if we don't have enough games yet
    if (windowGames.length < Math.min(window, 3)) {
      continue;
    }
    
    // Calculate batting averages for this window
    const stats = {
      date: sortedGames[i].date,
      gameId: sortedGames[i].id,
      windowSize: windowGames.length,
      battingStats: calculateWindowBattingStats(windowGames),
      pitchingStats: calculateWindowPitchingStats(windowGames)
    };
    
    rollingAverages.push(stats);
  }
  
  return rollingAverages;
};

/**
 * Calculate batting statistics for a window of games
 * @param {Array} windowGames - Array of game objects in the current window
 * @returns {Object} Batting statistics for the window
 */
const calculateWindowBattingStats = (windowGames) => {
  // Initialize stats
  const stats = {
    atBats: 0,
    hits: 0,
    doubles: 0,
    triples: 0,
    homeRuns: 0,
    walks: 0,
    strikeouts: 0,
    average: 0,
    obp: 0,
    slg: 0,
    ops: 0
  };
  
  // Sum up stats from all games in the window
  windowGames.forEach(game => {
    if (game.battingStats) {
      stats.atBats += Number(game.battingStats.atBats || 0);
      stats.hits += Number(game.battingStats.hits || 0);
      stats.doubles += Number(game.battingStats.doubles || 0);
      stats.triples += Number(game.battingStats.triples || 0);
      stats.homeRuns += Number(game.battingStats.homeRuns || 0);
      stats.walks += Number(game.battingStats.walks || 0);
      stats.strikeouts += Number(game.battingStats.strikeouts || 0);
    }
  });
  
  // Calculate derived stats
  if (stats.atBats > 0) {
    stats.average = stats.hits / stats.atBats;
    
    // Calculate total bases
    const totalBases = stats.hits + stats.doubles + (2 * stats.triples) + (3 * stats.homeRuns);
    stats.slg = totalBases / stats.atBats;
    
    // Calculate OBP
    const plateAppearances = stats.atBats + stats.walks;
    stats.obp = plateAppearances > 0 ? (stats.hits + stats.walks) / plateAppearances : 0;
    
    // Calculate OPS
    stats.ops = stats.obp + stats.slg;
  }
  
  return stats;
};

/**
 * Calculate pitching statistics for a window of games
 * @param {Array} windowGames - Array of game objects in the current window
 * @returns {Object} Pitching statistics for the window
 */
const calculateWindowPitchingStats = (windowGames) => {
  // Initialize stats
  const stats = {
    inningsPitched: 0,
    earnedRuns: 0,
    strikeouts: 0,
    walks: 0,
    hits: 0,
    era: 0,
    whip: 0
  };
  
  // Sum up stats from all games in the window
  windowGames.forEach(game => {
    if (game.pitchingStats) {
      stats.inningsPitched += Number(game.pitchingStats.inningsPitched || 0);
      stats.earnedRuns += Number(game.pitchingStats.earnedRuns || 0);
      stats.strikeouts += Number(game.pitchingStats.strikeouts || 0);
      stats.walks += Number(game.pitchingStats.walks || 0);
      stats.hits += Number(game.pitchingStats.hits || 0);
    }
  });
  
  // Calculate derived stats
  if (stats.inningsPitched > 0) {
    stats.era = (9 * stats.earnedRuns) / stats.inningsPitched;
    stats.whip = (stats.walks + stats.hits) / stats.inningsPitched;
  }
  
  return stats;
};

/**
 * Perform linear regression on a series of data points
 * @param {Array} data - Array of [x, y] pairs
 * @returns {Object} Regression results including slope, intercept, and r-squared
 */
const linearRegression = (data) => {
  const n = data.length;
  
  if (n < 2) {
    return { slope: 0, intercept: 0, rSquared: 0 };
  }
  
  // Calculate means
  let sumX = 0;
  let sumY = 0;
  
  for (let i = 0; i < n; i++) {
    sumX += data[i][0];
    sumY += data[i][1];
  }
  
  const meanX = sumX / n;
  const meanY = sumY / n;
  
  // Calculate slope and intercept
  let numerator = 0;
  let denominator = 0;
  
  for (let i = 0; i < n; i++) {
    const x = data[i][0];
    const y = data[i][1];
    
    numerator += (x - meanX) * (y - meanY);
    denominator += (x - meanX) * (x - meanX);
  }
  
  const slope = denominator !== 0 ? numerator / denominator : 0;
  const intercept = meanY - slope * meanX;
  
  // Calculate R-squared
  let totalSumOfSquares = 0;
  let residualSumOfSquares = 0;
  
  for (let i = 0; i < n; i++) {
    const x = data[i][0];
    const y = data[i][1];
    const prediction = slope * x + intercept;
    
    totalSumOfSquares += Math.pow(y - meanY, 2);
    residualSumOfSquares += Math.pow(y - prediction, 2);
  }
  
  const rSquared = totalSumOfSquares !== 0 ? 1 - (residualSumOfSquares / totalSumOfSquares) : 0;
  
  return { slope, intercept, rSquared };
};

/**
 * Implements a simple ARIMA-like model for time series forecasting
 * @param {Array} data - Array of values to forecast from
 * @param {number} p - AR order (default: 1)
 * @param {number} d - Differencing order (default: 1)
 * @param {number} q - MA order (default: 0)
 * @param {number} horizon - Number of steps to forecast (default: 1)
 * @returns {Array} Forecasted values
 */
const arimaForecast = (data, p = 1, d = 1, q = 0, horizon = 1) => {
  if (!data || data.length < p + d + q + 1) {
    return Array(horizon).fill(data[data.length - 1] || 0);
  }
  
  // Apply differencing
  let diffedData = [...data];
  for (let i = 0; i < d; i++) {
    const temp = [];
    for (let j = 1; j < diffedData.length; j++) {
      temp.push(diffedData[j] - diffedData[j - 1]);
    }
    diffedData = temp;
  }
  
  // Calculate AR coefficients using Yule-Walker equations (simplified)
  const arCoefficients = [];
  for (let i = 1; i <= p; i++) {
    let numerator = 0;
    let denominator = 0;
    
    for (let j = i; j < diffedData.length; j++) {
      numerator += diffedData[j] * diffedData[j - i];
      denominator += diffedData[j - i] * diffedData[j - i];
    }
    
    arCoefficients.push(denominator !== 0 ? numerator / denominator : 0);
  }
  
  // Forecast future values
  const forecasts = [];
  let currentData = [...diffedData];
  
  for (let h = 0; h < horizon; h++) {
    let forecast = 0;
    
    // Apply AR component
    for (let i = 0; i < p; i++) {
      if (currentData.length > i) {
        forecast += arCoefficients[i] * currentData[currentData.length - 1 - i];
      }
    }
    
    // Add forecast to data for next iteration
    currentData.push(forecast);
    forecasts.push(forecast);
  }
  
  // Reverse differencing
  let undiffedForecasts = [...forecasts];
  for (let i = 0; i < d; i++) {
    const lastOriginal = i === 0 ? data[data.length - 1] : undiffedForecasts[0];
    const temp = [lastOriginal];
    
    for (let j = 0; j < undiffedForecasts.length; j++) {
      temp.push(temp[j] + undiffedForecasts[j]);
    }
    
    undiffedForecasts = temp.slice(1);
  }
  
  return undiffedForecasts;
};

/**
 * Implements exponential smoothing for time series forecasting
 * @param {Array} data - Array of values to forecast from
 * @param {number} alpha - Smoothing factor (0-1)
 * @param {number} beta - Trend smoothing factor (0-1)
 * @param {number} horizon - Number of steps to forecast
 * @returns {Array} Forecasted values
 */
const exponentialSmoothing = (data, alpha = 0.3, beta = 0.1, horizon = 1) => {
  if (!data || data.length < 2) {
    return Array(horizon).fill(data[data.length - 1] || 0);
  }
  
  // Initialize level and trend
  let level = data[0];
  let trend = data[1] - data[0];
  
  // Apply smoothing
  for (let i = 1; i < data.length; i++) {
    const oldLevel = level;
    level = alpha * data[i] + (1 - alpha) * (level + trend);
    trend = beta * (level - oldLevel) + (1 - beta) * trend;
  }
  
  // Forecast future values
  const forecasts = [];
  for (let h = 1; h <= horizon; h++) {
    forecasts.push(level + h * trend);
  }
  
  return forecasts;
};

/**
 * Predict future performance based on recent trends using multiple forecasting methods
 * @param {Array} games - Array of game objects in chronological order
 * @param {Object} options - Configuration options
 * @returns {Object} Predicted stats for future games
 */
export const predictFuturePerformance = (games, options = {}) => {
  const {
    windowSize = 10,
    gamesAhead = 5,
    minGamesRequired = 5,
    considerFieldFactor = true,
    considerOpponentFactor = true,
    considerDayOfWeekFactor = true,
    forecastMethod = 'ensemble' // 'linear', 'arima', 'exponential', or 'ensemble'
  } = options;
  
  if (!games || games.length < minGamesRequired) {
    return {
      insufficientData: true,
      predictions: {
        avg: '.000',
        obp: '.000',
        slg: '.000'
      }
    };
  }
  
  // Sort games by date (ascending)
  const sortedGames = [...games].sort((a, b) => new Date(a.date) - new Date(b.date));
  
  // Get rolling averages
  const rollingAverages = calculateRollingAverages(sortedGames, windowSize);
  
  if (rollingAverages.length < minGamesRequired) {
    return {
      insufficientData: true,
      predictions: {
        avg: '.000',
        obp: '.000',
        slg: '.000'
      }
    };
  }
  
  // Extract time series data
  const avgSeries = rollingAverages.map(avg => avg.battingStats.average);
  const obpSeries = rollingAverages.map(avg => avg.battingStats.obp);
  const slgSeries = rollingAverages.map(avg => avg.battingStats.slg);
  
  // Prepare data for regression
  const avgData = rollingAverages.map((avg, index) => [index, avg.battingStats.average]);
  const obpData = rollingAverages.map((avg, index) => [index, avg.battingStats.obp]);
  const slgData = rollingAverages.map((avg, index) => [index, avg.battingStats.slg]);
  
  let predictedAvg, predictedObp, predictedSlg;
  
  // Apply selected forecasting method
  switch (forecastMethod) {
    case 'linear':
      // Linear regression
      const avgRegression = linearRegression(avgData);
      const obpRegression = linearRegression(obpData);
      const slgRegression = linearRegression(slgData);
      
      const lastIndex = rollingAverages.length - 1;
      predictedAvg = avgRegression.slope * (lastIndex + gamesAhead) + avgRegression.intercept;
      predictedObp = obpRegression.slope * (lastIndex + gamesAhead) + obpRegression.intercept;
      predictedSlg = slgRegression.slope * (lastIndex + gamesAhead) + slgRegression.intercept;
      break;
      
    case 'arima':
      // ARIMA-like forecasting
      const avgForecast = arimaForecast(avgSeries, 2, 1, 0, gamesAhead);
      const obpForecast = arimaForecast(obpSeries, 2, 1, 0, gamesAhead);
      const slgForecast = arimaForecast(slgSeries, 2, 1, 0, gamesAhead);
      
      predictedAvg = avgForecast[avgForecast.length - 1];
      predictedObp = obpForecast[obpForecast.length - 1];
      predictedSlg = slgForecast[slgForecast.length - 1];
      break;
      
    case 'exponential':
      // Exponential smoothing
      const avgExpForecast = exponentialSmoothing(avgSeries, 0.3, 0.1, gamesAhead);
      const obpExpForecast = exponentialSmoothing(obpSeries, 0.3, 0.1, gamesAhead);
      const slgExpForecast = exponentialSmoothing(slgSeries, 0.3, 0.1, gamesAhead);
      
      predictedAvg = avgExpForecast[avgExpForecast.length - 1];
      predictedObp = obpExpForecast[obpExpForecast.length - 1];
      predictedSlg = slgExpForecast[slgExpForecast.length - 1];
      break;
      
    case 'ensemble':
    default:
      // Ensemble method (average of multiple forecasting methods)
      // Linear regression
      const avgReg = linearRegression(avgData);
      const obpReg = linearRegression(obpData);
      const slgReg = linearRegression(slgData);
      
      const lastIdx = rollingAverages.length - 1;
      const avgLinear = avgReg.slope * (lastIdx + gamesAhead) + avgReg.intercept;
      const obpLinear = obpReg.slope * (lastIdx + gamesAhead) + obpReg.intercept;
      const slgLinear = slgReg.slope * (lastIdx + gamesAhead) + slgReg.intercept;
      
      // ARIMA-like forecasting
      const avgArima = arimaForecast(avgSeries, 2, 1, 0, gamesAhead)[gamesAhead - 1];
      const obpArima = arimaForecast(obpSeries, 2, 1, 0, gamesAhead)[gamesAhead - 1];
      const slgArima = arimaForecast(slgSeries, 2, 1, 0, gamesAhead)[gamesAhead - 1];
      
      // Exponential smoothing
      const avgExp = exponentialSmoothing(avgSeries, 0.3, 0.1, gamesAhead)[gamesAhead - 1];
      const obpExp = exponentialSmoothing(obpSeries, 0.3, 0.1, gamesAhead)[gamesAhead - 1];
      const slgExp = exponentialSmoothing(slgSeries, 0.3, 0.1, gamesAhead)[gamesAhead - 1];
      
      // Weighted average (give more weight to methods with better recent performance)
      const recentAvg = avgSeries[avgSeries.length - 1];
      const recentObp = obpSeries[obpSeries.length - 1];
      const recentSlg = slgSeries[slgSeries.length - 1];
      
      // Calculate weights based on how close each method's prediction is to recent performance
      const avgLinearWeight = 1 / Math.max(0.001, Math.abs(avgLinear - recentAvg));
      const avgArimaWeight = 1 / Math.max(0.001, Math.abs(avgArima - recentAvg));
      const avgExpWeight = 1 / Math.max(0.001, Math.abs(avgExp - recentAvg));
      const avgTotalWeight = avgLinearWeight + avgArimaWeight + avgExpWeight;
      
      const obpLinearWeight = 1 / Math.max(0.001, Math.abs(obpLinear - recentObp));
      const obpArimaWeight = 1 / Math.max(0.001, Math.abs(obpArima - recentObp));
      const obpExpWeight = 1 / Math.max(0.001, Math.abs(obpExp - recentObp));
      const obpTotalWeight = obpLinearWeight + obpArimaWeight + obpExpWeight;
      
      const slgLinearWeight = 1 / Math.max(0.001, Math.abs(slgLinear - recentSlg));
      const slgArimaWeight = 1 / Math.max(0.001, Math.abs(slgArima - recentSlg));
      const slgExpWeight = 1 / Math.max(0.001, Math.abs(slgExp - recentSlg));
      const slgTotalWeight = slgLinearWeight + slgArimaWeight + slgExpWeight;
      
      // Calculate weighted predictions
      predictedAvg = (
        (avgLinear * avgLinearWeight) + 
        (avgArima * avgArimaWeight) + 
        (avgExp * avgExpWeight)
      ) / avgTotalWeight;
      
      predictedObp = (
        (obpLinear * obpLinearWeight) + 
        (obpArima * obpArimaWeight) + 
        (obpExp * obpExpWeight)
      ) / obpTotalWeight;
      
      predictedSlg = (
        (slgLinear * slgLinearWeight) + 
        (slgArima * slgArimaWeight) + 
        (slgExp * slgExpWeight)
      ) / slgTotalWeight;
      break;
  }
  
  // Apply adjustments based on factors if enabled
  let adjustedAvg = predictedAvg;
  let adjustedObp = predictedObp;
  let adjustedSlg = predictedSlg;
  
  if (considerFieldFactor) {
    const fieldFactors = calculateFieldFactors(sortedGames);
    adjustedAvg *= fieldFactors.avgMultiplier;
    adjustedObp *= fieldFactors.obpMultiplier;
    adjustedSlg *= fieldFactors.slgMultiplier;
  }
  
  if (considerOpponentFactor) {
    const opponentFactors = calculateOpponentFactors(sortedGames);
    adjustedAvg *= opponentFactors.avgMultiplier;
    adjustedObp *= opponentFactors.obpMultiplier;
    adjustedSlg *= opponentFactors.slgMultiplier;
  }
  
  if (considerDayOfWeekFactor) {
    const dayOfWeekFactors = calculateDayOfWeekFactors(sortedGames);
    adjustedAvg *= dayOfWeekFactors.avgMultiplier;
    adjustedObp *= dayOfWeekFactors.obpMultiplier;
    adjustedSlg *= dayOfWeekFactors.slgMultiplier;
  }
  
  // Ensure predictions are within reasonable bounds
  const boundedAvg = Math.max(0, Math.min(1, adjustedAvg));
  const boundedObp = Math.max(0, Math.min(1, adjustedObp));
  const boundedSlg = Math.max(0, Math.min(2, adjustedSlg));
  
  // Format predictions
  const formattedAvg = boundedAvg.toFixed(3).replace(/^0+/, '');
  const formattedObp = boundedObp.toFixed(3).replace(/^0+/, '');
  const formattedSlg = boundedSlg.toFixed(3).replace(/^0+/, '');
  
  // Calculate confidence based on consistency of recent performance
  const calculateConfidence = (series) => {
    if (series.length < 5) return 0.5;
    
    // Calculate standard deviation of recent values
    const recentValues = series.slice(-5);
    const mean = recentValues.reduce((sum, val) => sum + val, 0) / recentValues.length;
    const variance = recentValues.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / recentValues.length;
    const stdDev = Math.sqrt(variance);
    
    // Normalize to a confidence value (lower stdDev = higher confidence)
    const normalizedStdDev = stdDev / (mean || 0.001); // Avoid division by zero
    return Math.max(0.1, Math.min(0.9, 1 - normalizedStdDev));
  };
  
  // Calculate trend direction and strength
  const calculateTrend = (series) => {
    if (series.length < 5) return { direction: 'stable', strength: 0 };
    
    const recentValues = series.slice(-5);
    const firstHalf = recentValues.slice(0, 2);
    const secondHalf = recentValues.slice(-2);
    
    const firstAvg = firstHalf.reduce((sum, val) => sum + val, 0) / firstHalf.length;
    const secondAvg = secondHalf.reduce((sum, val) => sum + val, 0) / secondHalf.length;
    
    const change = secondAvg - firstAvg;
    const percentChange = firstAvg !== 0 ? change / firstAvg : 0;
    
    let direction = 'stable';
    if (percentChange > 0.05) direction = 'up';
    else if (percentChange < -0.05) direction = 'down';
    
    return {
      direction,
      strength: Math.abs(percentChange)
    };
  };
  
  const avgConfidence = calculateConfidence(avgSeries);
  const obpConfidence = calculateConfidence(obpSeries);
  const slgConfidence = calculateConfidence(slgSeries);
  
  const avgTrend = calculateTrend(avgSeries);
  const obpTrend = calculateTrend(obpSeries);
  const slgTrend = calculateTrend(slgSeries);
  
  return {
    insufficientData: false,
    predictions: {
      avg: formattedAvg,
      obp: formattedObp,
      slg: formattedSlg,
      ops: (boundedObp + boundedSlg).toFixed(3).replace(/^0+/, '')
    },
    confidence: {
      avg: avgConfidence,
      obp: obpConfidence,
      slg: slgConfidence
    },
    trend: {
      avg: avgTrend.direction,
      obp: obpTrend.direction,
      slg: slgTrend.direction
    },
    trendStrength: {
      avg: avgTrend.strength,
      obp: obpTrend.strength,
      slg: slgTrend.strength
    },
    forecastMethod
  };
};

/**
 * Calculate field factors based on home/away performance
 * @param {Array} games - Array of game objects
 * @returns {Object} Field factors for adjusting predictions
 */
const calculateFieldFactors = (games) => {
  // Group games by home/away
  const homeGames = games.filter(game => game.isHome);
  const awayGames = games.filter(game => !game.isHome);
  
  // Default multipliers (no adjustment)
  const defaultFactors = {
    avgMultiplier: 1,
    obpMultiplier: 1,
    slgMultiplier: 1
  };
  
  // If not enough data to calculate factors, return defaults
  if (homeGames.length < 3 || awayGames.length < 3) {
    return defaultFactors;
  }
  
  // Calculate home stats
  const homeStats = calculateWindowBattingStats(homeGames);
  
  // Calculate away stats
  const awayStats = calculateWindowBattingStats(awayGames);
  
  // Calculate multipliers based on home/away performance
  // If home performance is better, multiplier will be > 1 for home games
  const avgRatio = homeStats.average / awayStats.average;
  const obpRatio = homeStats.obp / awayStats.obp;
  const slgRatio = homeStats.slg / awayStats.slg;
  
  // Determine if next game is likely home or away
  // For simplicity, we'll assume alternating pattern
  const lastGameIsHome = games[games.length - 1]?.isHome;
  const nextGameIsHome = !lastGameIsHome;
  
  // Apply multipliers based on next game location
  return {
    avgMultiplier: nextGameIsHome ? (isFinite(avgRatio) ? avgRatio : 1) : (isFinite(avgRatio) ? 1 / avgRatio : 1),
    obpMultiplier: nextGameIsHome ? (isFinite(obpRatio) ? obpRatio : 1) : (isFinite(obpRatio) ? 1 / obpRatio : 1),
    slgMultiplier: nextGameIsHome ? (isFinite(slgRatio) ? slgRatio : 1) : (isFinite(slgRatio) ? 1 / slgRatio : 1)
  };
};

/**
 * Calculate opponent strength factors
 * @param {Array} games - Array of game objects
 * @returns {Object} Opponent factors for adjusting predictions
 */
const calculateOpponentFactors = (games) => {
  // Group games by opponent
  const opponentMap = {};
  
  games.forEach(game => {
    if (!game.opponent) return;
    
    if (!opponentMap[game.opponent]) {
      opponentMap[game.opponent] = [];
    }
    
    opponentMap[game.opponent].push(game);
  });
  
  // Calculate performance against each opponent
  const opponentStats = {};
  
  Object.entries(opponentMap).forEach(([opponent, opponentGames]) => {
    if (opponentGames.length < 2) return;
    
    opponentStats[opponent] = calculateWindowBattingStats(opponentGames);
  });
  
  // Default multipliers (no adjustment)
  const defaultFactors = {
    avgMultiplier: 1,
    obpMultiplier: 1,
    slgMultiplier: 1
  };
  
  // If not enough data to calculate factors, return defaults
  if (Object.keys(opponentStats).length < 2) {
    return defaultFactors;
  }
  
  // Try to determine next opponent
  const recentOpponents = games.slice(-5).map(game => game.opponent);
  const nextOpponent = predictNextOpponent(recentOpponents);
  
  // If we can't determine next opponent, return defaults
  if (!nextOpponent || !opponentStats[nextOpponent]) {
    return defaultFactors;
  }
  
  // Calculate average stats across all opponents
  let totalAvg = 0;
  let totalObp = 0;
  let totalSlg = 0;
  let count = 0;
  
  Object.values(opponentStats).forEach(stats => {
    totalAvg += stats.average;
    totalObp += stats.obp;
    totalSlg += stats.slg;
    count++;
  });
  
  const avgAvg = count > 0 ? totalAvg / count : 0;
  const avgObp = count > 0 ? totalObp / count : 0;
  const avgSlg = count > 0 ? totalSlg / count : 0;
  
  // Calculate multipliers based on performance against next opponent vs. average
  const nextOpponentStats = opponentStats[nextOpponent];
  
  return {
    avgMultiplier: avgAvg > 0 ? nextOpponentStats.average / avgAvg : 1,
    obpMultiplier: avgObp > 0 ? nextOpponentStats.obp / avgObp : 1,
    slgMultiplier: avgSlg > 0 ? nextOpponentStats.slg / avgSlg : 1
  };
};
